import React, { useEffect } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { AutocompleteAddressInput } from 'components/autocomplete-address-input';
import { useAddressPredictions } from 'hooks/geolocation/use-address-predictions';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { TLocalizationKey } from 'types/i18n';
import { fadeIn } from 'utils/style/animations';

import { useDeliveryAddressModalContext } from '../delivery-address-modal.context';

const DeliveryAddressWrapper = styled.div`
  position: relative;
  margin-block-start: 1.5rem;
  margin-block-end: 1rem;
  margin-inline: 0;
  width: 100%;
`;
export const FadeIn = styled.div`
  animation: 0.2s ${fadeIn} forwards;
  opacity: 0;
`;

const DeliveryAutocompleteAddressInput = styled(AutocompleteAddressInput)`
  z-index: ${Styles.zIndex.below};
`;

type TDeliveryAddressLabelKeyId = Extract<
  TLocalizationKey,
  'deliveryAddress' | 'deliveryAddressStreetNumber'
>;

interface IDeliveryAddressFormInputProps {
  deliveryAddressInputRef: React.RefObject<HTMLInputElement>;
  labelKeyId?: TDeliveryAddressLabelKeyId;
  message: string;
  onAddressSelected: (newAddressString: string, placeId?: string) => void;
  onValueChange: (value: string) => void;
  setMessage: (message: string) => void;
  value: string;
}

export const DeliveryAddressFormInput: React.FC<IDeliveryAddressFormInputProps> = ({
  value,
  deliveryAddressInputRef,
  labelKeyId,
  message,
  onAddressSelected,
  setMessage,
  onValueChange,
}) => {
  const { formatMessage } = useIntl();

  const enableSaveDeliveryAddress = useFlag(LaunchDarklyFlag.ENABLE_SAVE_DELIVERY_ADDRESS);
  const enableMapFallBack = useFlag(LaunchDarklyFlag.ENABLE_MAP_FALLBACK);

  const { setDeliveryAddress } = useDeliveryAddressModalContext();

  const { predictions } = useAddressPredictions({
    pieceOfAddress: value,
    skip: !enableMapFallBack,
  });

  useEffect(() => {
    if (enableSaveDeliveryAddress && value) {
      setDeliveryAddress(undefined);
    }
    if (!value && deliveryAddressInputRef.current) {
      deliveryAddressInputRef.current.focus();
    }
  }, [value, deliveryAddressInputRef]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DeliveryAddressWrapper>
      <DeliveryAutocompleteAddressInput
        autoFocus
        data-private="redact"
        data-dd-privacy="mask"
        data-testid="delivery-address-input"
        label={formatMessage({ id: labelKeyId || 'deliveryAddress' })}
        matchTypes={[]}
        predictions={predictions}
        message={message}
        onAddressSelected={onAddressSelected}
        onInputValueChange={onValueChange}
        ref={deliveryAddressInputRef}
        required
        setMessage={setMessage}
        shouldExpandPostalCodeToAddresses
        value={value}
      />
    </DeliveryAddressWrapper>
  );
};
